import React from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { GridRowParams } from '@material-ui/data-grid';
import { IconButton, ListItem, Typography } from '@material-ui/core';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import ActionsMenu from '../../components/other/ActionsMenu';
import { gridColumn } from '../../utils/gridUtils';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes } from '../../utils/constants';
import TextLink from 'components/other/TextLink';
import { dlmDetailsTabParameters, DLM_DETAILS_TABS } from 'utils/routing';
import { Api } from 'api';
import { usePaginatedProvider } from 'state/paginatedProviderStore';
import { useProvider } from 'state/providerStore';
import { toJS } from 'mobx';

const LocationDetailsPowerSource = observer(() => {
  const { locationid } = useParams<{ locationid: string }>();

  const powerSourceProvider = usePaginatedProvider(() =>
    Api.charge.listPowerSourcesForLocation(locationid).catch((err) => {
      console.error('Error fetching power sources:', err);
    }),
  );

  const strategiesProvider = useProvider(() =>
    Api.charge.listDLMStrategies().catch((err) => {
      console.error('Error fetching DLM strategies:', err);
    }),
  );

  const unassignedChargerProvider = useProvider(() =>
    Api.charge.listUnassignedChargePointsForLocation(locationid).catch((err) => {
      console.error('Error fetching unassigned charge points:', err);
    }),
  );

  const allChargerProvider = useProvider(() =>
    Api.charge.listChargersFromLocation(locationid).catch((err) => {
      console.error('Error fetching all charge points:', err);
    }),
  );

  const getStrategyOptions = () =>
    strategiesProvider.data?.map((s) => ({
      label: s.name,
      value: s.id,
    })) ?? [];

  const getChargePointOptions = () =>
    unassignedChargerProvider.data?.map((cp) => ({
      label: cp.ocppId,
      value: cp.id,
      bottomRow: cp.id,
    })) ?? [];

  const powerSourceDrawerInputs = [
    { name: 'name', label: 'Name', type: FormInputTypes.textField },
    { name: 'voltage', label: 'Voltage (V)', type: FormInputTypes.number },
    { name: 'current', label: 'Current (A)', type: FormInputTypes.number },
    { name: 'power', label: 'Power (kW)', type: FormInputTypes.number },
    {
      name: 'strategyId',
      label: 'DLM Strategy',
      type: FormInputTypes.select,
      options: getStrategyOptions(),
    },
    {
      name: 'chargePoints',
      label: 'Charge Points',
      type: FormInputTypes.checkboxSelect,
      options: getChargePointOptions(),
    },
  ];

  const onClickConfirmAddPowerSource = async () => {
    const values = toJS(drawerStore.values);
    const { chargePoints, ...powerSourceData } = values;

    try {
      const response = await Api.charge.createPowerSource({
        ...powerSourceData,
        locationId: locationid,
      });
      const newPowerSourceId = response.data?.data?.id;

      await Promise.all(
        chargePoints.map((cpId: string) =>
          Api.charge.patchCharger(cpId, { powerSourcesId: newPowerSourceId }),
        ),
      );

      powerSourceProvider.reloadResource();
      unassignedChargerProvider.reloadResource();
      allChargerProvider.reloadResource();
    } catch (err) {
      console.error('Error adding power source:', err);
    }
  };

  const onClickConfirmEditPowerSource = (powerSourceId: string) => async () => {
    const values = toJS(drawerStore.values);
    const { chargePoints, ...updateData } = values;

    try {
      await Api.charge.updatePowerSource(powerSourceId, updateData);

      await Promise.all(
        chargePoints.map((cpId: string) =>
          Api.charge.patchCharger(cpId, { powerSourcesId: powerSourceId }),
        ),
      );

      powerSourceProvider.reloadResource();
      unassignedChargerProvider.reloadResource();
      allChargerProvider.reloadResource();
    } catch (err) {
      console.error('Error editing power source:', err);
    }
  };

  const handleAdd = () => {
    drawerStore.openDrawer({
      label: 'Add Power Source',
      inputs: powerSourceDrawerInputs,
      clickCallback: onClickConfirmAddPowerSource,
    });
    drawerStore.setValues({});
  };

  const handleEdit = (row: any) => {
    drawerStore.openDrawer({
      label: 'Edit Power Source',
      inputs: powerSourceDrawerInputs,
      clickCallback: onClickConfirmEditPowerSource(row.id),
    });

    drawerStore.setValues(row);
  };

  const handleRemove = async (row: any) => {
    const assignedChargePoints =
      allChargerProvider.data?.filter((cp) => cp.powerSourcesId === row.id) ?? [];

    const assignedChargePointIds = assignedChargePoints.map((cp) => cp.id);

    if (assignedChargePointIds.length > 0) {
      await Api.charge.unassignPowerSources(assignedChargePointIds);
    }

    await Api.charge.deletePowerSource(row.id);
    powerSourceProvider.reloadResource();
    allChargerProvider.reloadResource?.();
    unassignedChargerProvider.reloadResource?.();
  };

  const powerSourceActions = (params: GridRowParams) => (
    <ActionsMenu>
      <ListItem button onClick={() => handleEdit(params.row)}>
        <Typography>Edit</Typography>
      </ListItem>
      <ListItem button onClick={() => handleRemove(params.row)}>
        <Typography>Remove</Typography>
      </ListItem>
    </ActionsMenu>
  );

  const onClickChargersCount = (row) => {
    const assigned = allChargerProvider.data?.filter((cp) => cp.powerSourcesId === row.id) ?? [];

    const options = assigned.map((cp) => ({
      topRow: cp.ocppId,
      bottomRow: cp.id,
    }));

    drawerStore.openDrawer({
      label: 'Assigned Charge Points',
      inputs: [
        {
          name: 'assignedChargePoints',
          label: '',
          type: FormInputTypes.textLinkView,
          options,
        },
      ],
      clickCallback: undefined,
      buttonLabel: 'Close',
    });

    drawerStore.setValues({ assignedChargePoints: options.map((o) => o.value) });
  };

  const powerSourceColumns = [
    gridColumn('name', {
      headerName: 'Name',
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <TextLink
          to={dlmDetailsTabParameters(params.row.strategyId, DLM_DETAILS_TABS.OVERVIEW)}
          label={params.row.name}
        />
      ),
    }),
    gridColumn('voltage', {
      headerName: 'Voltage',
      flex: 1,
      minWidth: 150,
      valueGetter: (p) => `${p.row.voltage} V`,
    }),
    gridColumn('current', {
      headerName: 'Current',
      flex: 1,
      minWidth: 150,
      valueGetter: (p) => `${p.row.current} A`,
    }),
    gridColumn('power', {
      headerName: 'Power',
      flex: 1,
      valueGetter: (p) => `${p.row.power} kW`,
      minWidth: 150,
    }),
    gridColumn('chargePointCount', {
      headerName: 'Charge Points',
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <IconButton
          onClick={() => onClickChargersCount(params.row)}
          style={{ padding: 0, justifyContent: 'center', alignItems: 'center' }}
        >
          <Typography color="primary">{params.row.chargePointCount ?? 0}</Typography>
        </IconButton>
      ),
    }),
    gridColumn('strategyName', { headerName: 'DLM Strategy', flex: 2, minWidth: 150 }),
    gridColumn('', {
      width: 60,
      renderCell: powerSourceActions,
      align: 'left',
      headerAlign: 'left',
    }),
  ];

  return (
    <div style={{ width: '100%' }}>
      <VMPaginatedGrid
        rows={powerSourceProvider.data || []}
        columns={powerSourceColumns}
        label="Power Sources"
        count={powerSourceProvider.count}
        onClickAdd={handleAdd}
        rowHeight={60}
        onPageOffsetChange={powerSourceProvider.onPageChange}
      />
    </div>
  );
});

export default LocationDetailsPowerSource;
